import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { FuseguLogo } from "@/components/FuseguLogo"
import { ArrowLeft, CheckCircle, XCircle, DollarSign, Clock, Users } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

export default function SucuriAlternative() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <Helmet>
        <title>Sucuri Alternative - Multi-Site Scanning for Agencies | Fusegu</title>
        <meta
          name="description"
          content="Looking for a Sucuri alternative for agencies? Fusegu offers comprehensive security scanning for unlimited sites at $588/year vs $200-500/year per site for Sucuri. Try free."
        />
        <meta property="og:title" content="Sucuri Alternative - Unlimited Sites Security Scanning" />
        <meta property="og:description" content="Switch from Sucuri to Fusegu for multi-site agencies. Comprehensive scanning, white-label reports, and unlimited sites at one flat rate." />
        <meta property="og:url" content="https://fusegusecurity.com/sucuri-alternative" />
        <meta name="keywords" content="Sucuri alternative, website security, malware scanning, vulnerability detection, affordable security" />
      </Helmet>
      <div className="flex min-h-screen w-full flex-col">
        {/* Header */}
        <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
          <div className="flex items-center justify-between w-full">
            <Link to="/" className="flex items-center gap-2">
              <FuseguLogo className="h-6 w-6 flex-shrink-0" />
              <span className="text-lg font-bold">Fusegu</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button>Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </header>

        <main className="flex-1 py-12 px-4 md:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
          <div className="max-w-4xl mx-auto">
            {/* Back to home link */}
            <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6">
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Link>

            {/* Article Header */}
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Sucuri Alternative - Unlimited Sites for Growing Agencies
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed">
                Looking for a Sucuri alternative? Fusegu offers comprehensive security scanning for
                unlimited sites at one flat rate - perfect for agencies managing multiple client websites.
              </p>
            </div>

            {/* Quick Value Props */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="border-2 border-green-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-6 w-6 text-green-600" />
                    <CardTitle className="text-lg">Multi-Site Value</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-green-600">$588/yr</p>
                  <p className="text-sm text-gray-600">vs $200-500/yr per site</p>
                  <p className="text-sm font-semibold mt-2">Unlimited sites</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Users className="h-6 w-6 text-blue-600" />
                    <CardTitle className="text-lg">Unlimited Sites</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-blue-600">1,500/mo</p>
                  <p className="text-sm text-gray-600">vs 1 site per plan</p>
                  <p className="text-sm font-semibold mt-2">Scale your agency</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="h-6 w-6 text-purple-600" />
                    <CardTitle className="text-lg">On-Demand</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-purple-600">5-10 min</p>
                  <p className="text-sm text-gray-600">Instant results</p>
                  <p className="text-sm font-semibold mt-2">Scan anytime</p>
                </CardContent>
              </Card>
            </div>

            {/* Why Teams Switch */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">Why Teams Switch from Sucuri to Fusegu</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Better Value for Multi-Site Agencies</p>
                    <p className="text-gray-600">Sucuri charges $200-500/year per site. Fusegu is $588/year for unlimited sites - massive savings once you have 3+ client sites.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Scan Unlimited Client Sites</p>
                    <p className="text-gray-600">Pay once, scan all your clients. No per-site fees means you can scale your agency without increasing security costs.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Instant Scanning</p>
                    <p className="text-gray-600">Get comprehensive security results in 5-10 minutes. No waiting for scheduled scans.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">White-Label Client Reports</p>
                    <p className="text-gray-600">Generate professional PDF reports with your agency branding included in the base plan.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Transparent Pricing</p>
                    <p className="text-gray-600">Clear, simple pricing with no hidden fees or per-site charges. Know exactly what you'll pay.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Comparison Table */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">Sucuri vs Fusegu</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Feature</TableHead>
                        <TableHead>Sucuri Platform</TableHead>
                        <TableHead>Fusegu</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Price (Basic)</TableCell>
                        <TableCell>$199.99/year per site</TableCell>
                        <TableCell className="font-bold text-green-600">$588/year unlimited</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Sites Included</TableCell>
                        <TableCell>1 site</TableCell>
                        <TableCell className="font-bold text-green-600">Unlimited (50 scans/day)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Scan Frequency</TableCell>
                        <TableCell>Every 12 hours (Basic)</TableCell>
                        <TableCell className="font-bold text-green-600">On-demand anytime</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">White-label Reports</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="text-sm">Not available</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Included</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Security Scanners</TableCell>
                        <TableCell>Sucuri proprietary</TableCell>
                        <TableCell className="font-bold">10+ tools (OWASP ZAP, Nuclei, SSL Labs, etc.)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">SOC2 Compliance</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">CDN/Firewall</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span>Scanning only</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Malware Removal</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span>Detection only</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">DDoS Protection</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* When Sucuri is Better */}
            <Card className="mb-8 border-2 border-orange-200">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <span>When Sucuri is Better</span>
                  <Badge variant="outline" className="ml-2">Honest Comparison</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Sucuri is excellent for:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Active malware removal</strong> - Sucuri will clean infected sites for you</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>CDN and WAF protection</strong> - Includes firewall and content delivery network</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>DDoS protection</strong> - Protects against distributed denial of service attacks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>All-in-one security</strong> - Complete protection + monitoring + response</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>High-value single sites</strong> - If you only manage 1-2 critical sites</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* When Fusegu is Better */}
            <Card className="mb-8 border-2 border-green-200">
              <CardHeader>
                <CardTitle className="text-2xl text-green-600">When Fusegu is Better</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Fusegu is perfect for:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Agencies managing multiple sites</strong> - Unlimited scanning vs $199/month per site with Sucuri</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Budget-conscious teams</strong> - Save $1,800/year while scanning unlimited sites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Vulnerability scanning focus</strong> - If you need detection but handle remediation yourself</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Client reporting</strong> - White-labeled security reports with your branding</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>On-demand scanning</strong> - Scan anytime you need, not just on Sucuri's schedule</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Compliance requirements</strong> - SOC2 compliance mapping included</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Pricing Comparison */}
            <Card className="mb-8 border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="text-2xl">Pricing Comparison: Real-World Scenarios</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <p className="text-lg font-semibold mb-4">Annual Cost for 10 Client Sites:</p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="p-4 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-600">Sucuri Platform (Basic)</p>
                        <p className="text-3xl font-bold">$1,999/year</p>
                        <p className="text-sm text-gray-600">10 sites × $199.99/year</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border-2 border-green-300">
                        <p className="text-sm text-green-700">Fusegu</p>
                        <p className="text-3xl font-bold text-green-600">$588/year</p>
                        <p className="text-sm text-green-700">Unlimited sites</p>
                      </div>
                    </div>
                    <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
                      <p className="text-2xl font-bold text-green-700 mb-2">You Save: $1,411/year</p>
                      <p className="text-gray-700">With Fusegu, scan unlimited sites at one flat rate. Perfect for growing agencies.</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-lg font-semibold mb-4">Annual Cost for 20 Client Sites:</p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="p-4 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-600">Sucuri Platform (Basic)</p>
                        <p className="text-3xl font-bold">$3,998/year</p>
                        <p className="text-sm text-gray-600">20 sites × $199.99/year</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border-2 border-green-300">
                        <p className="text-sm text-green-700">Fusegu</p>
                        <p className="text-3xl font-bold text-green-600">$588/year</p>
                        <p className="text-sm text-green-700">Unlimited sites</p>
                      </div>
                    </div>
                    <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
                      <p className="text-2xl font-bold text-green-700 mb-2">You Save: $3,410/year</p>
                      <p className="text-gray-700">The savings increase dramatically as your agency grows.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="font-semibold text-lg mb-2">Can Fusegu clean malware like Sucuri does?</p>
                  <p className="text-gray-700">
                    No, Fusegu focuses on detection and vulnerability scanning. We'll identify malware and security
                    issues, but you'll need to clean them yourself or hire a specialist. This is why Fusegu is much
                    more affordable - we don't include remediation services.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">Does Fusegu include a firewall?</p>
                  <p className="text-gray-700">
                    No, Fusegu is a scanning and detection tool, not a firewall or CDN service. For firewall
                    protection, consider using Cloudflare (free plan works great) alongside Fusegu for scanning.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">How is Fusegu so much cheaper than Sucuri?</p>
                  <p className="text-gray-700">
                    Sucuri includes CDN, WAF, DDoS protection, malware removal, and 24/7 monitoring. Fusegu focuses
                    solely on automated scanning and vulnerability detection. By specializing, we can offer excellent
                    scanning at a fraction of the cost.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">What does Fusegu actually scan for?</p>
                  <p className="text-gray-700">
                    Fusegu runs 10+ security scanners including OWASP ZAP, SSL Labs, Nuclei, and Nmap. We detect
                    OWASP Top 10 vulnerabilities, SSL/TLS issues, security headers, outdated software, WordPress-specific
                    problems, and infrastructure vulnerabilities.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">Can I use Fusegu alongside Sucuri?</p>
                  <p className="text-gray-700">
                    Absolutely! Some agencies use Sucuri for high-value sites that need full protection, and Fusegu
                    for regular vulnerability scanning on all other client sites. This hybrid approach saves money
                    while maintaining security.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">How quickly can I switch from Sucuri to Fusegu?</p>
                  <p className="text-gray-700">
                    Immediately! Just sign up and start scanning. Since Fusegu scans externally, there's nothing to
                    install or migrate. You can run both services in parallel during a transition period.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* CTA Section */}
            <Card className="mb-8 bg-gradient-to-br from-blue-600 to-purple-700 text-white border-none">
              <CardContent className="p-8 text-center">
                <h2 className="text-3xl font-bold mb-4">Try Fusegu Free - No Credit Card Required</h2>
                <p className="text-xl mb-6 text-blue-50">
                  See how much you'll save. Scan your first site in 5 minutes.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8">
                      Start Free Trial
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button size="lg" className="bg-blue-700 text-white hover:bg-blue-800 font-semibold px-8 border-2 border-white">
                      View Demo
                    </Button>
                  </Link>
                </div>
                <p className="text-sm mt-4 text-blue-100">
                  Join agencies saving thousands by switching from Sucuri
                </p>
              </CardContent>
            </Card>

          </div>
        </main>

        {/* Footer */}
        <footer className="border-t py-8 px-4 md:px-8 bg-white">
          <div className="max-w-4xl mx-auto text-center text-sm text-gray-600">
            <p className="mb-4">Fusegu Security | fusegusecurity.com</p>
            <div className="flex justify-center gap-6 flex-wrap">
              <Link to="/privacy" className="hover:text-blue-600">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-blue-600">Terms of Service</Link>
              <Link to="/blogs" className="hover:text-blue-600">Blog</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
